import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState } from "react";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  TruncateWithTooltip
} from "shared/components";
import { Modal } from "shared/components/Modal";
import { Banner } from "shared/components/Banner";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { IncomeStreamCard } from "shared/components/IncomeStreamCard";
import { IncomeSource } from "shared/types";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { getIncomeJobName, incomeTypeMap } from "../../utils";
import { CombinedIncome } from "../../types/income";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

export type SelectBankStreamModalData = {
  incomeId: number;
  income: CombinedIncome;
  availableSource: IncomeSource;
  assignedItemId: number | null;
};

type Props = {
  opened: boolean;
  onClose: () => void;
  applicant: Applicant;
  applicationId: number;
  verificationId: number;
  onSuccess: () => void | Promise<unknown>;
  data: SelectBankStreamModalData;
};

export const SelectBankStreamModal = ({
  opened,
  onClose,
  applicant,
  applicationId,
  verificationId,
  onSuccess,
  data
}: Props) => {
  const { incomeId, income, availableSource, assignedItemId } = data;
  const queryClient = useQueryClient();

  const [selectedItemId, setSelectedItemId] = useState<number | null>(
    assignedItemId
  );

  const { mutate, isPending } = useMutation({
    mutationKey: [
      "CHANGE_INCOME_STREAM",
      applicationId,
      verificationId,
      applicant.id,
      incomeId
    ],
    mutationFn: (itemId: number) =>
      instance
        .post(
          `/portal/v1/applications/${applicationId}/verifications/${verificationId}/income/${incomeId}/assign_items`,
          { applicant_id: applicant.id, item_ids: [itemId] }
        )
        .then(({ data }) => data),
    onError: (err) => toast.error(getErrorMessage(err)),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({ queryKey: ["REPORTS"] });
      await onSuccess();
      toast.success("Income stream was successfully updated");
      onClose();
    }
  });

  const isUnchanged =
    selectedItemId === null || selectedItemId === assignedItemId;

  const handleSave = () => {
    if (!isUnchanged) mutate(selectedItemId);
  };

  const applicantName = getFullName({
    firstName: applicant.first_name,
    lastName: applicant.last_name,
    middleName: applicant.middle_name,
    short: true
  });
  const jobName = getIncomeJobName(income.additional_data);
  const typeLabel = incomeTypeMap[income.type];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[560px] w-full"
    >
      <header className="px-6 py-4 min-h-[80px] rounded-t-lg border-b border-b-alpha-40">
        <div className="text-xl font-bold flex justify-between items-center leading-8">
          Change income sources
          <IconButton
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.LG}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={onClose}
            aria-label="Close modal"
          />
        </div>
        <p className="flex items-center gap-2 text-content-400 font-medium text-sm leading-5 min-w-0">
          <TruncateWithTooltip
            content={applicantName}
            className="min-w-0 flex-shrink"
            tooltipClassName="z-[9999]"
          />
          {jobName ? (
            <>
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
              <TruncateWithTooltip
                content={jobName}
                className="min-w-0 flex-shrink"
                tooltipClassName="z-[9999]"
              />
            </>
          ) : null}
          {typeLabel ? (
            <>
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
              <span className="shrink-0">{typeLabel}</span>
            </>
          ) : null}
        </p>
      </header>

      <Banner
        variant="warning"
        textTheme="base"
        borders="bottom"
        body="Editing income may affect all summary metrics and decision recommendations"
        HugeIcon={
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="shrink-0 text-warning-500"
            size="20"
          />
        }
      />

      <div className="px-6 py-5 space-y-3 max-h-[480px] overflow-y-auto bg-background-900">
        {availableSource.items.map((item) => {
          const isAssignedToOtherIncome =
            item.income_id !== null && item.income_id !== incomeId;

          return (
            <IncomeStreamCard
              key={item.id}
              item={item}
              isSelected={selectedItemId === item.id}
              onSelect={() => setSelectedItemId(item.id)}
              disabled={isAssignedToOtherIncome}
              showPayFrequency
              trailing={
                item.selected_by_applicant ? (
                  <Badge
                    variant={BadgeVariant.BASE}
                    size={BadgeSize.SM}
                    borderless={false}
                    className="bg-alpha-40"
                  >
                    Selected by applicant
                  </Badge>
                ) : null
              }
            />
          );
        })}
      </div>

      <div className="px-6 py-4 flex gap-2 justify-end border-t border-alpha-100">
        <Button className="w-[125px]" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="w-[138px]"
          variant={ButtonVariant.PRIMARY}
          disabled={isUnchanged}
          loading={isPending}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};
